import { AfterViewChecked, AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { TableFilterStateService } from '../../services/table-filter-state.service';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SharedTable } from '../../models/table.model';
import { MatTableExporterDirective } from 'mat-table-exporter';
import { isDiscardedRow, isFailOrAlertRow, isOutlierRow } from '../../utils/failed-rows.util';
import { toCsv } from '../../utils/csv.util';
import { saveBlobAsFile } from '../../utils/helper';
/**
 * SharedTableComponent 
 * This component is a reusable, configurable data table built with Angular Material.
 * It supports dynamic column rendering, sorting, filtering, and CSV export functionality.
 */
@Component({
  selector: 'app-shared-table',
  templateUrl: './shared-table.component.html',
  styleUrl: './shared-table.component.scss'
})


export class SharedTableComponent implements OnInit, AfterViewInit, AfterViewChecked, OnChanges, OnDestroy {

  private readonly filterStateService = inject(TableFilterStateService);
  /** When set, filter selections are kept across the table being destroyed (e.g. a tab switch). */
  @Input() filterStateKey?: string;

  @Input() sharedTableContent!: SharedTable;
  @Input() customCssClassBinding!: (element: { [key: string]: string }, key: string) => { [cssClass: string]: boolean };
  @Input() checkboxColumnKey?: string;
  /** Holds the CSV export back until the study has been saved. */
  @Input() csvExportDisabled = false;
  @Input() checkboxChecked?: (element: { [key: string]: unknown }) => boolean;
  @Input() checkboxChange?: (element: { [key: string]: unknown }, checked: boolean) => void;
  @Input() checkboxDisabled?: (element: { [key: string]: unknown }) => boolean;
  @Input() showFailuresOnlyToggle = false;
  /** Always applies the failures filter and hides its checkbox. */
  @Input() forceFailuresOnly = false;
  @Input() dropdownFilterKeys: string[] = [];
  @Input() isSuperUser = false;
  /** Columns holding a Calibrator Adjustment's Final run, tinted in place of the adjusted ones. */
  @Input() finalColumnKeys: string[] = [];
  /** Builds what the CSV button downloads when that should differ from the rows shown. Called on click. */
  @Input() exportContentProvider?: () => SharedTable;
  @Input() exportFileName = 'table';
  /** Columns that stay in view on horizontal scroll, in place of the default of just the first one. */
  @Input() pinnedColumnKeys: string[] = [];

  @ViewChild('sharedTable', { read: ElementRef }) sharedTable!: ElementRef;
  @ViewChild(MatSort) sort!: MatSort;

  tableDataSource!: MatTableDataSource<unknown>;
  displayColumns = new Array<string>();
  filterText = '';
  failuresOnlyChecked = false;
  columnExcludedValues = new Map<string, Set<string>>();
  allColumnValues = new Map<string, string[]>();
  private columnFilterVersion = 0;
  private stateRestored = false;
  private resetSub?: Subscription;
  private pinnedResizeObserver?: ResizeObserver;
  private observedHeaders = new Set<HTMLElement>();
  /**
    * Detects changes to input data. Updates displayed columns and table data source.
    */
  ngOnChanges(): void {
    if (this.sharedTableContent) {
      this.restoreFilterState();
      this.displayColumns = this.sharedTableContent.keys;

      // Reuse the data source to avoid reinitializing sort/filter on every update.
      if (this.tableDataSource) {
        this.tableDataSource.data = this.sharedTableContent.data;
      } else {
        this.tableDataSource = new MatTableDataSource(this.sharedTableContent.data);
      }
      this.rebuildColumnValues();
      this.tableDataSource.filterPredicate = this.buildFilterPredicate();
      this.applyFilters();
    }
  }
  /**
    * Lifecycle hook to set up sorting after view is initialized.
    */
  ngAfterViewInit() {
    if (this.tableDataSource) {
      this.tableDataSource.sort = this.sort;
    }
  }
  ngAfterViewChecked(): void {
    if (!this.pinnedColumnKeys.length || !this.sharedTable) {
      return;
    }
    // Column widths keep changing after change detection (data, fonts, filtering), so the pinned
    // headers are watched and the offsets re-measured whenever one resizes.
    this.pinnedResizeObserver ??= new ResizeObserver(() => this.measurePinnedOffsets());
    const table = this.sharedTable.nativeElement as HTMLElement;
    const headers = new Set(this.pinnedHeaders(table));
    this.observedHeaders.forEach(header => {
      if (!headers.has(header)) {
        this.pinnedResizeObserver?.unobserve(header);
        this.observedHeaders.delete(header);
      }
    });
    headers.forEach(header => {
      if (!this.observedHeaders.has(header)) {
        this.pinnedResizeObserver?.observe(header);
        this.observedHeaders.add(header);
      }
    });
    this.measurePinnedOffsets();
  }

  private pinnedHeaders(table: HTMLElement): HTMLElement[] {
    return this.pinnedKeysInOrder()
      .map(key => table.querySelector<HTMLElement>(`th.mat-column-${key}`))
      .filter((header): header is HTMLElement => !!header);
  }

  /** Each pinned column's `left` is the combined width of the pinned columns before it. */
  private measurePinnedOffsets(): void {
    const table = this.sharedTable?.nativeElement as HTMLElement | undefined;
    if (!table) {
      return;
    }
    let left = 0;
    this.pinnedKeysInOrder().forEach((key, index) => {
      const header = table.querySelector<HTMLElement>(`th.mat-column-${key}`);
      if (!header) {
        return;
      }
      // A CSS variable on the table, so the cells need no re-render when an offset changes.
      const offset = `${left}px`;
      if (table.style.getPropertyValue(`--pin-left-${index}`) !== offset) {
        table.style.setProperty(`--pin-left-${index}`, offset);
      }
      left += header.getBoundingClientRect().width;
    });
  }

  private pinnedKeysInOrder(): string[] {
    return this.displayColumns.filter(key => this.pinnedColumnKeys.includes(key));
  }

  /**
   * Classes for a pinned cell: the pin itself and its position among the pinned columns, which the
   * stylesheet turns into `left`. Not an inline style, because the table's sticky handling clears those.
   */
  pinClass(key: string): string {
    const index = this.pinnedKeysInOrder().indexOf(key);
    return index < 0 ? '' : `pinned-col pinned-col-${index}`;
  }

  ngOnInit(): void {
    this.resetSub = this.filterStateService.reset$.subscribe(() => {
      this.filterText = '';
      this.failuresOnlyChecked = false;
      this.columnExcludedValues.clear();
      this.columnFilterVersion += 1;
      this.applyFilters();
    });
  }

  ngOnDestroy(): void {
    this.resetSub?.unsubscribe();
    this.pinnedResizeObserver?.disconnect();
    if (this.filterStateKey) {
      this.filterStateService.save(this.filterStateKey, {
        filterText: this.filterText,
        failuresOnlyChecked: this.failuresOnlyChecked,
        columnExcludedValues: this.columnExcludedValues
      });
    }
  }
  /**
   * Filters the table data using the current filter text.
   */
  filterData() {
    this.applyFilters();
  }
  /**
    * Triggers table export to CSV using mat-table-exporter.
    * Also suppresses known error about undefined properties from external library.
    * 
    * @param exporter - MatTableExporterDirective used for export functionality
    */
  exportToCSV(exporter: MatTableExporterDirective) {
    if (this.exportContentProvider) {
      this.downloadCsv(this.exportContentProvider());
      return;
    }
    const originalConsoleError = console.error.bind(console);
    console.error = (message: unknown, ...args: unknown[]) => {
      if (typeof message === 'string' && message.includes('Cannot set properties of undefined')) {
        return;
      }
      originalConsoleError(message, ...args);
    };
    try {
      exporter.exportTable('csv', { fileName: this.exportFileName });
    } finally {
      console.error = originalConsoleError;
    }
  }

  /** Every row and column of the given content, with the cells formatted as the table shows them. */
  private downloadCsv(content: SharedTable): void {
    const headers = content.keys.map(key => content.columns[key]);
    const rows = content.data.map(row =>
      content.keys.map(key => this.formatCellValue((row as Record<string, unknown>)[key]))
    );
    saveBlobAsFile(new Blob([toCsv(headers, rows)], { type: 'text/csv;charset=utf-8' }), `${this.exportFileName}.csv`);
  }

  // getColumnClass(key: string): string {
  //   const columnName = this.sharedTableContent?.columns?.[key];
  //   return columnName?.includes('adjusted') ? 'adjusted-highlight' : ''; 
  // }
  /**
     * Returns a CSS class for the table cell based on the column key.
     * Used to apply highlight to adjusted result columns.
     * 
     * @param key - The column key
     * @returns string - CSS class to apply
     */
  getCellClass(key: string): string {
    if (this.finalColumnKeys.includes(key)) {
      return 'final-highlight';
    }
    const excludedKeys = ['adjfinalValue'];

    if (key?.toLowerCase()?.includes('adj') && !excludedKeys.includes(key)) {
      return 'adjusted-highlight';
    }
    return '';
  }

  isCheckboxChecked(element: { [key: string]: unknown }): boolean {
    return this.checkboxChecked ? this.checkboxChecked(element) : false;
  }

  isCheckboxDisabled(element: { [key: string]: unknown }): boolean {
    return this.checkboxDisabled ? this.checkboxDisabled(element) : false;
  }

  onCheckboxChange(element: { [key: string]: unknown }, event: MatCheckboxChange): void {
    this.checkboxChange?.(element, event.checked);
  }

  formatCellValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '-';
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed ? trimmed : '-';
    }
    if (typeof value === 'number') {
      return Number.isFinite(value) ? String(value) : '-';
    }
    return String(value);
  }

  getUniqueColumnValues(key: string): string[] {
    return this.allColumnValues.get(key) ?? [];
  }

  isColumnValueChecked(key: string, value: string): boolean {
    const excluded = this.columnExcludedValues.get(key);
    return !excluded || !excluded.has(value);
  }

  toggleColumnValue(key: string, value: string): void {
    if (!this.columnExcludedValues.has(key)) {
      this.columnExcludedValues.set(key, new Set());
    }
    const excluded = this.columnExcludedValues.get(key)!;
    if (excluded.has(value)) {
      excluded.delete(value);
    } else {
      excluded.add(value);
    }
    this.columnFilterVersion += 1;
    this.applyFilters();
  }

  isColumnFilterActive(key: string): boolean {
    const excluded = this.columnExcludedValues.get(key);
    return !!excluded && excluded.size > 0 && excluded.size < this.getUniqueColumnValues(key).length;
  }

  areAllSelected(key: string): boolean {
    const excluded = this.columnExcludedValues.get(key);
    return !excluded || excluded.size === 0;
  }

  toggleSelectAll(key: string): void {
    if (this.areAllSelected(key)) {
      this.columnExcludedValues.set(key, new Set(this.getUniqueColumnValues(key)));
    } else {
      this.columnExcludedValues.set(key, new Set());
    }
    this.columnFilterVersion += 1;
    this.applyFilters();
  }

  applyFilters(): void {
    if (!this.tableDataSource) {
      return;
    }
    const filterState = {
      text: this.filterText.trim().toLowerCase(),
      failuresOnly: this.forceFailuresOnly || (this.showFailuresOnlyToggle ? this.failuresOnlyChecked : false),
      v: this.columnFilterVersion
    };
    this.tableDataSource.filter = JSON.stringify(filterState);
  }

  isFailOrAlertRow(element: { [key: string]: unknown }): boolean {
    return isFailOrAlertRow(element);
  }

  isDiscardedRow(element: { [key: string]: unknown }): boolean {
    return isDiscardedRow(element);
  }

  isOutlierRow(element: { [key: string]: unknown }): boolean {
    return isOutlierRow(element);
  }

  private restoreFilterState(): void {
    if (this.stateRestored || !this.filterStateKey) {
      return;
    }
    this.stateRestored = true;
    const saved = this.filterStateService.get(this.filterStateKey);
    if (saved) {
      this.filterText = saved.filterText;
      this.failuresOnlyChecked = saved.failuresOnlyChecked;
      this.columnExcludedValues = saved.columnExcludedValues;
      this.columnFilterVersion += 1;
    }
  }

  private rebuildColumnValues(): void {
    // Keep existing exclusions across data refreshes, pruned to values still present.
    const previous = this.columnExcludedValues;
    this.columnExcludedValues = new Map();
    if (!this.dropdownFilterKeys?.length || !this.sharedTableContent?.data) {
      return;
    }
    this.dropdownFilterKeys.forEach(key => {
      const seen = new Set<string>();
      this.sharedTableContent.data.forEach(row => {
        const val = this.formatCellValue((row as Record<string, unknown>)[key]);
        seen.add(val);
      });
      const sorted = Array.from(seen).sort((a, b) => a.localeCompare(b));
      this.allColumnValues.set(key, sorted);
      const kept = Array.from(previous.get(key) ?? []).filter(v => seen.has(v));
      this.columnExcludedValues.set(key, new Set(kept));
    });
  }

  private buildFilterPredicate(): (data: unknown, filter: string) => boolean {
    return (data: unknown, filter: string): boolean => {
      let textFilter = '';
      let failuresOnly = false;
      if (filter) {
        try {
          const parsed = JSON.parse(filter) as { text?: string; failuresOnly?: boolean };
          textFilter = (parsed.text ?? '').toLowerCase();
          failuresOnly = Boolean(parsed.failuresOnly);
        } catch {
          textFilter = filter.toLowerCase();
        }
      }

      const record = data as Record<string, unknown>;

      // Discarded replicates are worth reviewing alongside failures: the result was computed from
      // fewer replicates than the run specified, even where every spec passed.
      if (failuresOnly && !this.isFailOrAlertRow(record) && !this.isDiscardedRow(record)) {
        return false;
      }

      for (const [key, excluded] of this.columnExcludedValues) {
        if (!excluded.size) {
          continue;
        }
        const cellVal = this.formatCellValue(record[key]);
        if (excluded.has(cellVal)) {
          return false;
        }
      }

      if (!textFilter) {
        return true;
      }

      const haystack = Object.values(record)
        .map(value => (value === null || value === undefined ? '' : String(value)))
        .join(' ')
        .toLowerCase();

      return haystack.includes(textFilter);
    };
  }

}
